import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { FC, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { BackButton } from '../../components/molecules/BackButton'
import { useJahisNsipsMismatchesQuery } from '../../features/jahisNsipsMatching'
import {
  JahisNsipsMismatch,
  FIELD_LABELS,
} from '../../types/models/JahisNsipsMatching'

/**
 * 不一致処方箋行
 */
const MismatchRow: FC<{
  mismatch: JahisNsipsMismatch
  onCopyRequestId: (id: string) => void
}> = ({ mismatch, onCopyRequestId }) => {
  return (
    <>
      {mismatch.fields.map((field, index) => (
        <TableRow key={`${mismatch.prescription_id}-${field.field}-${index}`}>
          {index === 0 && (
            <>
              <TableCell rowSpan={mismatch.fields.length}>
                {mismatch.prescription_date || '(データなし)'}
              </TableCell>
              <TableCell rowSpan={mismatch.fields.length}>
                {mismatch.institution_name || '(データなし)'}
              </TableCell>
            </>
          )}
          <TableCell
            sx={{
              bgcolor: 'rgba(255, 235, 238, 0.5)',
              fontFamily: 'monospace',
            }}
          >
            {field.jahis_value || '(空)'}
          </TableCell>
          <TableCell
            sx={{
              bgcolor: 'rgba(232, 245, 233, 0.5)',
              fontFamily: 'monospace',
            }}
          >
            {field.nsips_value || '(空)'}
          </TableCell>
          {index === 0 && (
            <TableCell rowSpan={mismatch.fields.length} align="center">
              <Tooltip title="Request IDをコピー">
                <IconButton
                  size="small"
                  onClick={() => onCopyRequestId(mismatch.request_event_id)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          )}
        </TableRow>
      ))}
    </>
  )
}

/**
 * JAHIS-NSIPS突合 不一致詳細一覧ページ
 */
export const JahisNsipsMismatchesPage: FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // URLパラメータから取得
  const startDate =
    searchParams.get('start_date') ||
    dayjs().subtract(30, 'day').format('YYYY-MM-DD')
  const endDate = searchParams.get('end_date') || dayjs().format('YYYY-MM-DD')
  const field = searchParams.get('field') || undefined
  const pharmacyId = searchParams.get('pharmacy_id') || undefined

  // 不一致詳細取得
  const {
    data: mismatches,
    isPending,
    isError,
    error,
  } = useJahisNsipsMismatchesQuery(startDate, endDate, field, pharmacyId)

  // Request IDをクリップボードにコピー
  const handleCopyRequestId = async (requestEventId: string) => {
    try {
      await navigator.clipboard.writeText(requestEventId)
      setCopiedId(requestEventId)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      console.error('Failed to copy to clipboard')
    }
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100%',
        py: 4,
        px: { xs: 2, sm: 4 },
        overflow: 'auto',
        maxWidth: '100vw',
        boxSizing: 'border-box',
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          position: 'fixed',
          zIndex: 1300,
          left: 0,
          top: 0,
          bgcolor: 'rgba(66, 66, 66, 0.75)',
          color: '#fff',
          p: 0.8,
          height: '36px',
          borderRadius: '0px 0px 8px 0px',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0px 2px 4px rgba(0,0,0,0.15)',
          backdropFilter: 'blur(2px)',
          paddingRight: '12px',
        }}
      >
        <CompareArrowsIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
        <Typography variant="body2">
          JAHIS-NSIPS突合 不一致詳細
          {field && ` - ${FIELD_LABELS[field] || field}`}
        </Typography>
      </Box>

      {/* ローディング */}
      {isPending && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Box sx={{ mt: 6, mb: 10, maxWidth: '1400px', mx: 'auto' }}>
        {/* 条件表示 */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            期間: {startDate} 〜 {endDate}
            {field && ` | 項目: ${FIELD_LABELS[field] || field}`}
          </Typography>
        </Paper>

        {/* エラー表示 */}
        {isError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            データの取得に失敗しました: {(error as Error)?.message}
          </Alert>
        )}

        {/* 不一致データ */}
        {mismatches && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              不一致一覧 ({mismatches.total_count.toLocaleString()}件)
            </Typography>

            {mismatches.mismatches.length === 0 ? (
              <Alert severity="success">不一致はありません</Alert>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>処方日</TableCell>
                        <TableCell>医療機関</TableCell>
                        <TableCell sx={{ bgcolor: 'rgba(255, 235, 238, 0.3)' }}>
                          JAHIS値
                        </TableCell>
                        <TableCell sx={{ bgcolor: 'rgba(232, 245, 233, 0.3)' }}>
                          NSIPS値
                        </TableCell>
                        <TableCell align="center">ID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mismatches.mismatches.map((mismatch) => (
                        <MismatchRow
                          key={mismatch.prescription_id}
                          mismatch={mismatch}
                          onCopyRequestId={handleCopyRequestId}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {copiedId && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Request ID をコピーしました: {copiedId}
                  </Alert>
                )}
              </>
            )}
          </Paper>
        )}
      </Box>

      <BackButton
        text="統計に戻る"
        onClick={() => navigate('/portal/jahis-nsips-stats')}
      />
    </Box>
  )
}
